import {Hierarchy} from '@entities/common/common.entity';
import {FilterOption} from '@entities/global-filters/global-filters.interfaces';

/**
 * 'MapTreeHelper' class definition.
 */
export class MapTreeHelper {
	/**
	 * Build tree.
	 * @param {T[]} data
	 * @param {keyof T} idKey
	 * @param {keyof T} nameKey
	 * @param {keyof T} codeKey
	 * @param {keyof T} contentCodeKey
	 * @param {boolean} useSorting
	 * @returns {FilterOption[]}
	 */
	public static buildTree<T extends Hierarchy>(
		data: T[],
		idKey: keyof T,
		nameKey: keyof T,
		codeKey: keyof T,
		contentCodeKey?: keyof T,
		useSorting = true
	): FilterOption[] {
		const itemsMap: Record<string, number> = {};
		const children: Record<string, string[]> = {root: []};

		for (let idx = 0; idx < data.length; idx++) {
			const itemId = `${String(data[idx][idKey])}`;
			itemsMap[itemId] = idx;
			children[itemId] = [];
		}

		data.forEach((node) => {
			if (node.PARENT_ID === '0') {
				children.root.push(`${String(node[idKey])}`);
				return;
			}
			const listIdx = itemsMap[node.PARENT_ID];
			children[`${String(data[listIdx][idKey])}`].push(`${String(node[idKey])}`);
		});

		const result = [];
		const treeBuilder = (itemId: string, level = 0): void => {
			const indent = Array(level).fill('    ').join('');

			if (nameKey === 'PRODUCT_OWNER_DESC') {
				children[itemId]
					.map((childId: string) => [childId, `${String(data[itemsMap[childId]][nameKey])}`])
					.forEach(([childId]) => {
						result.push({
							id: data[itemsMap[childId]][idKey],
							code: data[itemsMap[childId]][codeKey],
							title: data[itemsMap[childId]][nameKey],
							name: `${indent}${String(data[itemsMap[childId]][nameKey])}`,
							contentCode: data[itemsMap[childId]][contentCodeKey],
							level,
							parentId: itemId,
						});
						treeBuilder(childId, level + 1);
					});
			} else {
				const childrenItems = children[itemId].map((childId: string) => [
					childId,
					`${String(data[itemsMap[childId]][nameKey])}`,
				]);
				const sortedChildren = useSorting
					? childrenItems.sort((a, b) => a[1].localeCompare(b[1]))
					: childrenItems;

				sortedChildren.forEach(([childId]) => {
					result.push({
						id: data[itemsMap[childId]][idKey],
						code: data[itemsMap[childId]][codeKey],
						title: data[itemsMap[childId]][nameKey],
						name: `${indent}${String(data[itemsMap[childId]][nameKey])}`,
						contentCode: data[itemsMap[childId]][contentCodeKey],
						level,
						parentId: itemId,
					});
					treeBuilder(childId, level + 1);
				});
			}
		};
		treeBuilder('root');
		return result;
	}
}
